import React, { useState } from 'react';
import {
  UserPlus,
  Search,
  SlidersHorizontal,
  Mail,
  Phone,
  MapPin,
  Calendar,
  MoreVertical,
  Edit,
  Trash2,
  FileText,
  Archive,
  Shield,
  Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AddEmployeeModal from '../modals/AddEmployeeModal';
import DeleteEmployeeModal from '../modals/DeleteEmployeeModal';

/**
 * Carte Premium Intervenant (Interne)
 */
const PremiumIntervenantCard = ({ employee, roles, getRoleName, onAction, delay, hasPermission, isOnline }) => {
  const [showMenu, setShowMenu] = useState(false);

  // Fonction pour gérer le clic sur le menu sans déclencher l'action de la carte
  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Fermer le menu au clic ailleurs
  React.useEffect(() => {
    const closeMenu = () => setShowMenu(false);
    if (showMenu) window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, [showMenu]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay }}
      className="group relative bg-white/80 dark:bg-slate-800/40 backdrop-blur-md rounded-2xl border border-gray-200 dark:border-slate-700/50 hover:bg-white dark:hover:bg-slate-800/60 hover:border-blue-500/30 transition-all duration-300 overflow-visible"
    >
      {/* Top Banner Effect */}
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-blue-900/10 to-transparent rounded-t-2xl pointer-events-none" />

      <div className="p-6">
        {/* Header: Avatar, Name, Role */}
        <div className="flex items-start justify-between relative z-10">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-xl font-bold text-gray-600 dark:text-slate-300 overflow-hidden border-2 border-gray-300 dark:border-slate-600 group-hover:border-blue-400/50 transition-colors shadow-lg">
                {employee.photoURL ? (
                  <img src={employee.photoURL} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="bg-gradient-to-br from-blue-600 to-indigo-600 w-full h-full flex items-center justify-center text-white">
                    {employee.firstName?.[0]}{employee.lastName?.[0]}
                  </span>
                )}
              </div>
              <div
                title={
                  employee.isArchived ? 'Archivé' :
                  employee.isActive === false ? 'Inactif' :
                  isOnline ? 'En ligne' : 'Hors ligne'
                }
                className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center ${
                  employee.isActive === false || employee.isArchived
                    ? 'bg-slate-500'
                    : isOnline
                      ? 'bg-emerald-500'
                      : 'bg-emerald-600/60'
                }`}
              >
                {isOnline && employee.isActive !== false && !employee.isArchived && (
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors">
                {employee.firstName} {employee.lastName}
              </h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-700/50 text-blue-700 dark:text-blue-200 border border-gray-200 dark:border-slate-600">
                  {getRoleName(employee.role)}
                </span>
                {employee.isArchived && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700/50">
                    <Archive size={10} /> Archivé
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="relative">
            <button
              onClick={handleMenuClick}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <MoreVertical size={20} />
            </button>

            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden"
                >
                  <div className="py-1">
                    <button onClick={(e) => { e.stopPropagation(); onAction('view', employee); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white flex items-center gap-2">
                      <UserPlus size={16} /> Voir le profil
                    </button>
                    {(!hasPermission || hasPermission({ resource: 'intervenants', action: 'write' })) && (
                      <button onClick={(e) => { e.stopPropagation(); onAction('edit', employee); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white flex items-center gap-2">
                        <Edit size={16} /> Modifier
                      </button>
                    )}
                    {(!hasPermission || hasPermission({ resource: 'intervenants', action: 'write' })) && (
                      <>
                        <div className="h-px bg-gray-200 dark:bg-slate-700 my-1"></div>
                        <button onClick={(e) => { e.stopPropagation(); onAction('archive', employee); }} className="w-full text-left px-4 py-2 text-sm text-amber-500 hover:bg-amber-900/20 hover:text-amber-400 flex items-center gap-2">
                          <Archive size={16} /> {employee.isArchived ? 'Désarchiver' : 'Archiver'}
                        </button>
                      </>
                    )}
                    {(!hasPermission || hasPermission({ resource: 'intervenants', action: 'delete' })) && (
                      <>
                        <div className="h-px bg-gray-200 dark:bg-slate-700 my-1"></div>
                        <button onClick={(e) => { e.stopPropagation(); onAction('delete', employee); }} className="w-full text-left px-4 py-2 text-sm text-rose-400 hover:bg-rose-900/20 hover:text-rose-300 flex items-center gap-2">
                          <Trash2 size={16} /> Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-6 space-y-3">
          {employee.email && (
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-slate-400 group-hover:text-gray-700 dark:group-hover:text-slate-300 transition-colors">
              <div className="p-1.5 rounded-md bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 group-hover:bg-blue-900/20 group-hover:text-blue-400 transition-colors">
                <Mail size={14} />
              </div>
              <span className="truncate">{employee.email}</span>
            </div>
          )}
          {employee.phoneNumber && (
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-slate-400 group-hover:text-gray-700 dark:group-hover:text-slate-300 transition-colors">
              <div className="p-1.5 rounded-md bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 group-hover:bg-emerald-900/20 group-hover:text-emerald-400 transition-colors">
                <Phone size={14} />
              </div>
              <span>{employee.phoneNumber}</span>
            </div>
          )}
        </div>

        {/* Status & City */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-700/50 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-gray-400 dark:text-slate-500">
            <MapPin size={14} />
            <span>{employee.address?.city || 'Localisation inconnue'}</span>
          </div>

          <div className="flex gap-2">
            <button onClick={() => onAction('schedule', employee)} className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-blue-600 hover:text-white text-gray-400 dark:text-slate-400 transition-all tooltip" title="Planning">
              <Calendar size={16} />
            </button>
            <button onClick={() => onAction('report', employee)} className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-purple-600 hover:text-white text-gray-400 dark:text-slate-400 transition-all tooltip" title="Rapports">
              <FileText size={16} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Composant Intervenants - Premium Redesign
 */
const IntervenantsModule = ({
  filteredEmployees,
  employees,
  roles,
  searchTerm,
  showAdvancedFilters,
  advancedFilters,
  handleSearchChange,
  toggleAdvancedFilters,
  handleAdvancedFiltersChange,
  getRoleName,
  hasPermission,
  appointments,
  onlineStatuses,
  onAction,
  db,
  currentUser
}) => {
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showDeleteEmployeeModal, setShowDeleteEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const uniqueRoleIds = [...new Set(employees.map(emp => emp.role).filter(Boolean))];

  const handleActionInternal = (action, employee) => {
    switch (action) {
      case 'delete':
        setSelectedEmployee(employee);
        setShowDeleteEmployeeModal(true);
        break;
      default:
        if (onAction) onAction(action, employee);
        break;
    }
  };

  const handleDeleteSuccess = (deletedEmployee) => {
    setShowDeleteEmployeeModal(false);
    setSelectedEmployee(null);
    alert(`Intervenant ${deletedEmployee.firstName} ${deletedEmployee.lastName} supprimé avec succès`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 p-6 font-sans">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              Gestion des Intervenants
            </h1>
            <p className="text-gray-500 dark:text-slate-400 mt-1">{filteredEmployees.length} intervenants trouvés</p>
          </div>

          {hasPermission({ resource: 'employees', action: 'create' }) && (
            <button
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl shadow-lg shadow-blue-500/20 transition-all font-medium"
              onClick={() => setShowAddEmployeeModal(true)}
            >
              <UserPlus size={18} />
              <span>Nouvel intervenant</span>
            </button>
          )}
        </div>

        {/* Search & Filters Bar */}
        <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-sm p-4 rounded-xl border border-gray-200 dark:border-slate-700/50 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom, email, rôle..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg pl-12 pr-4 py-3 text-gray-800 dark:text-slate-200 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              />
            </div>

            <button
              onClick={toggleAdvancedFilters}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg border transition-all font-medium ${showAdvancedFilters
                  ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                  : 'bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-750 hover:text-gray-700 dark:hover:text-slate-200'
                }`}
            >
              <SlidersHorizontal size={18} />
              Filtres
            </button>
          </div>

          {/* Advanced Filters Panel */}
          <AnimatePresence>
            {showAdvancedFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden pt-2"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700/50">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Statut</label>
                    <select
                      value={advancedFilters.status}
                      onChange={(e) => handleAdvancedFiltersChange('status', e.target.value)}
                      className="w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-gray-800 dark:text-slate-200 outline-none focus:border-blue-500"
                    >
                      <option value="active">Actifs</option>
                      <option value="inactive">Inactifs</option>
                      <option value="archived">Archivés</option>
                      <option value="all">Tous</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Rôle</label>
                    <select
                      value={advancedFilters.role}
                      onChange={(e) => handleAdvancedFiltersChange('role', e.target.value)}
                      className="w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-gray-800 dark:text-slate-200 outline-none focus:border-blue-500"
                    >
                      <option value="all">Tous les rôles</option>
                      {uniqueRoleIds.map(roleId => (
                        <option key={roleId} value={roleId}>{getRoleName(roleId)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredEmployees.map((employee, index) => (
              <PremiumIntervenantCard
                key={employee.id}
                employee={employee}
                roles={roles}
                getRoleName={getRoleName}
                onAction={handleActionInternal}
                delay={index * 0.05}
                hasPermission={hasPermission}
                isOnline={onlineStatuses?.[employee.id] ?? false}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {filteredEmployees.length === 0 && (
          <div className="text-center py-16 bg-gray-50 dark:bg-slate-800/20 rounded-2xl border border-gray-200 dark:border-slate-700/50 border-dashed">
            <div className="w-20 h-20 mx-auto bg-gray-200 dark:bg-slate-800/50 rounded-full flex items-center justify-center text-gray-400 dark:text-slate-600 mb-4">
              <Search size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-700 dark:text-slate-300 mb-2">Aucun intervenant trouvé</h3>
            <p className="text-gray-400 dark:text-slate-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}

        {/* Modals */}
        {showAddEmployeeModal && (
          <AddEmployeeModal
            db={db}
            onClose={() => setShowAddEmployeeModal(false)}
            onSuccess={() => setShowAddEmployeeModal(false)} // Firestore listener updates list automatically
          />
        )}

        {showDeleteEmployeeModal && selectedEmployee && (
          <DeleteEmployeeModal
            employee={selectedEmployee}
            onClose={() => {
              setShowDeleteEmployeeModal(false);
              setSelectedEmployee(null);
            }}
            onSuccess={handleDeleteSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default IntervenantsModule;
